import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArchiveSheet,
  ClueList,
  CluesSheet,
  MenuSheet,
  ShareSheet,
  SolvedSheet,
  TopSheet,
} from '../Components/CrosswordSheets'
import {
  BackspaceIcon,
  CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  DotsIcon,
  Spinner,
  TimerIcon,
} from '../Components/Icons'
import { useRail, useRailSlot } from '../Components/Layout'
import useCrossword from '../Hooks/useCrossword'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import { t } from '../i18n'
import { api } from '../Services/api'
import { useUi } from '../State/Ui'
import {
  ACROSS,
  buildPuzzle,
  cellKey,
  formatCount,
  formatTime,
  KEYBOARD_ROWS,
  pruneProgress,
  shortDate,
  weekdayDate,
} from '../Utils/crossword'

const SITE_PATH = '/krizaljka'

/* /krizaljka and /krizaljka/{datum}: the daily mini crossword (a new one at
   07:00). Desktop types on the keyboard, phones get the on-screen one. */
export default function Crossword() {
  const { date } = useParams()
  const [state, setState] = useState({ status: 'loading', puzzle: null })
  const [reload, setReload] = useState(0)
  const nextAt = state.puzzle?.nextAt
  useDocumentTitle(t('crossword.documentTitle'))
  useRail({ slot: true, search: false })

  // Today's page left open past 07:00: the new puzzle when it's looked at again.
  useEffect(() => {
    if (date || !nextAt) return undefined
    const check = () => {
      if (!document.hidden && Date.now() >= Date.parse(nextAt)) setReload((n) => n + 1)
    }
    document.addEventListener('visibilitychange', check)
    window.addEventListener('focus', check)
    return () => {
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('focus', check)
    }
  }, [date, nextAt])

  useEffect(() => {
    let alive = true
    setState({ status: 'loading', puzzle: null })
    ;(date ? api.crossword(date) : api.crosswordToday())
      .then((data) => {
        if (!alive) return
        setState({ status: 'ready', puzzle: buildPuzzle(data) })
        if (data.isToday) pruneProgress(data.date)
      })
      .catch(
        (e) =>
          alive &&
          setState({ status: e?.response?.status === 404 ? 'missing' : 'error', puzzle: null })
      )
    return () => {
      alive = false
    }
  }, [date, reload])

  if (state.status === 'loading')
    return (
      <div className="center-block cw-loading">
        <Spinner size={22} />
      </div>
    )
  if (state.status !== 'ready')
    return (
      <div className="feed-message cw-error">
        <p>
          {state.status === 'missing'
            ? date
              ? t('crossword.notFound')
              : t('crossword.notReady')
            : t('crossword.loadError')}
        </p>
        {date && (
          <Link className="link-orange" to={SITE_PATH}>
            {t('crossword.backToToday')}
          </Link>
        )}
      </div>
    )
  return <Game key={state.puzzle.date} puzzle={state.puzzle} />
}

function Game({ puzzle }) {
  const navigate = useNavigate()
  const { isMobile, notify } = useUi()
  const railSlot = useRailSlot()
  const [sheet, setSheet] = useState(null) // top | menu | archive | solved | share | clues
  const [share, setShare] = useState(null) // what the share picture shows
  const game = useCrossword(puzzle, { onSolved: () => setSheet('solved') })
  const { progress, sel, current } = game
  const isToday = puzzle.isToday
  const close = useCallback(() => setSheet(null), [])

  // Typing goes to the grid from anywhere on the page (not from a field, not
  // while a sheet is open).
  useEffect(() => {
    if (sheet) return undefined
    const onKey = (e) => {
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
      if (e.key === 'Tab' && !e.target?.closest?.('.cw-grid')) return // Tab elsewhere moves focus
      game.onKey(e)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [sheet, game])

  // null = today's puzzle.
  const pickDay = (day) => {
    close()
    navigate(day ? `${SITE_PATH}/${day}` : SITE_PATH)
  }

  let message = ''
  let messageClass = ''
  if (progress.solved)
    message = t('crossword.solvedLine', { time: formatTime(game.result?.seconds ?? progress.secs) })
  else if (game.checked) {
    if (game.wrong) {
      message = t('crossword.wrong', { count: game.wrong })
      messageClass = 'bad'
    } else if (game.filled) message = t('crossword.allCorrect')
  }

  const clueList = <ClueList game={game} puzzle={puzzle} onPick={game.pickWord} />
  // After a solve the server's count includes this one.
  const solvedCount = game.result?.solvedCount ?? puzzle.solvedCount

  return (
    <div className={`cw-page ${isMobile ? 'mobile' : ''}`}>
      <h1 className="sr-only">{t('crossword.title')}</h1>
      <header className="cw-header">
        <button
          type="button"
          className="cw-chip"
          onClick={() => setSheet('archive')}
          aria-label={t('crossword.openArchive', { date: weekdayDate(puzzle.date) })}
        >
          <CalendarIcon size={16} className="muted-icon" />
          {isMobile ? shortDate(puzzle.date) : weekdayDate(puzzle.date)}
          <ChevronDown size={12} strokeWidth={2.6} className="muted-icon" />
        </button>
        <span className="cw-timer" role="timer" aria-label={t('crossword.timer')}>
          <TimerIcon size={16} className="muted-icon" />
          {formatTime(game.result?.alreadySolved ? game.result.seconds : progress.secs)}
        </span>
        <span className="cw-spacer" />
        <button
          type="button"
          className="cw-solvers"
          onClick={() => setSheet('top')}
          aria-label={t('crossword.openTop', { n: formatCount(solvedCount) })}
        >
          <strong>{formatCount(solvedCount)}</strong>{' '}
          {isMobile
            ? t('crossword.solvedShort')
            : isToday
            ? t('crossword.solvedToday')
            : t('crossword.solvedThatDay')}
          <ChevronRight size={12} strokeWidth={2.6} />
        </button>
        <button
          type="button"
          className="icon-button cw-more"
          onClick={() => setSheet('menu')}
          aria-label={t('crossword.more')}
        >
          <DotsIcon size={18} />
        </button>
      </header>

      {!isToday && (
        <p className="cw-archive-note">
          {t('crossword.archiveDay', { date: weekdayDate(puzzle.date) })}{' '}
          <Link className="link-orange" to={SITE_PATH}>
            {t('crossword.backToToday')}
          </Link>
        </p>
      )}

      <div className="cw-body" style={{ '--cw-cols': puzzle.cols, '--cw-rows': puzzle.rows }}>
        <Grid puzzle={puzzle} game={game} />

        <div className="cw-clue-bar">
          <button
            type="button"
            className="cw-clue-arrow"
            onClick={() => game.goWord(-1)}
            aria-label={t('crossword.prevClue')}
          >
            <ChevronLeft size={18} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className="cw-clue-main"
            onClick={game.toggleDir}
            aria-label={t('crossword.toggleDir')}
          >
            {current && (
              <>
                <span className="cw-clue-label">
                  {current.number}{' '}
                  {current.dir === ACROSS ? t('crossword.acrossShort') : t('crossword.downShort')}
                </span>
                <span className="cw-clue-current">{current.text}</span>
              </>
            )}
          </button>
          <button
            type="button"
            className="cw-clue-arrow"
            onClick={() => game.goWord(1)}
            aria-label={t('crossword.nextClue')}
          >
            <ChevronRight size={18} strokeWidth={2.4} />
          </button>
        </div>

        <div className="cw-actions">
          {isMobile && (
            <button type="button" className="cw-all-clues" onClick={() => setSheet('clues')}>
              {t('crossword.allClues')} ›
            </button>
          )}
          <span className={`cw-message ${messageClass}`} role="status">
            {message}
          </span>
          {progress.solved ? (
            <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={() => setSheet('solved')}
            >
              {t('crossword.result')}
            </button>
          ) : (
            <button type="button" className="btn btn-secondary btn-small" onClick={game.check}>
              {t('crossword.check')}
            </button>
          )}
        </div>

        {!isMobile && (
          <p className="cw-hints">
            <span>{t('crossword.hintClick')}</span>
            <span>{t('crossword.hintArrows')}</span>
            <span>{t('crossword.hintSpace')}</span>
            <span>{t('crossword.hintTab')}</span>
          </p>
        )}

        {/* Desktop without the right column (below 1100 px): clues under the grid. */}
        {!isMobile && !railSlot && clueList}
      </div>

      {isMobile && <Keyboard game={game} />}

      {railSlot &&
        createPortal(
          <>
            {clueList}
            <p className="cw-daily muted">{t('crossword.daily')}</p>
          </>,
          railSlot
        )}

      {sheet === 'top' && (
        <TopSheet puzzle={puzzle} isToday={isToday} game={game} onClose={close} notify={notify} />
      )}
      {sheet === 'menu' && <MenuSheet game={game} onClose={close} />}
      {sheet === 'archive' && (
        <ArchiveSheet current={puzzle.date} onPick={pickDay} onClose={close} />
      )}
      {sheet === 'solved' && (
        <SolvedSheet
          puzzle={puzzle}
          isToday={isToday}
          game={game}
          onClose={close}
          onShare={(data) => {
            setShare(data)
            setSheet('share')
          }}
        />
      )}
      {sheet === 'share' && share && (
        <ShareSheet
          puzzle={puzzle}
          game={game}
          share={share}
          onClose={() => setSheet('solved')}
          notify={notify}
        />
      )}
      {sheet === 'clues' && <CluesSheet game={game} puzzle={puzzle} onClose={close} />}
      <span className="sr-only" aria-live="polite">
        {sel && current
          ? `${current.number} ${
              current.dir === ACROSS ? t('crossword.acrossShort') : t('crossword.downShort')
            }: ${current.text}`
          : ''}
      </span>
    </div>
  )
}

function Grid({ puzzle, game }) {
  const { progress, sel, current, checked } = game
  const [sr, sc] = sel
  const inCurrent = (r, c) => Boolean(current?.cells.some(([y, x]) => y === r && x === c))
  return (
    <div
      className="cw-grid"
      role="grid"
      aria-label={t('crossword.gridLabel', { rows: puzzle.rows, cols: puzzle.cols })}
    >
      {Array.from({ length: puzzle.rows }, (_, r) => (
        <div role="row" className="cw-row" key={r}>
          {Array.from({ length: puzzle.cols }, (_, c) => {
            if (puzzle.isBlack(r, c))
              return <span key={c} className="cw-cell black" aria-hidden="true" />
            const k = cellKey(r, c)
            const letter = progress.entries[k] || ''
            const number = puzzle.numbers[k]
            const isSel = r === sr && c === sc
            const wrong = checked && letter && letter !== puzzle.solution(r, c)
            const revealed = Boolean(progress.revealed[k])
            const label = [
              number ? `${number}` : null,
              t('crossword.cellLabel', { row: r + 1, col: c + 1 }),
              letter ? t('crossword.cellLetter', { letter }) : t('crossword.cellEmpty'),
            ]
              .filter(Boolean)
              .join(', ')
            return (
              <button
                key={c}
                type="button"
                role="gridcell"
                tabIndex={isSel ? 0 : -1}
                aria-selected={isSel}
                aria-label={label}
                className={`cw-cell ${isSel ? 'selected' : inCurrent(r, c) ? 'in-word' : ''} ${
                  wrong ? 'wrong' : ''
                } ${revealed ? 'revealed' : ''} ${c === puzzle.cols - 1 ? 'last-col' : ''} ${
                  r === puzzle.rows - 1 ? 'last-row' : ''
                }`}
                onClick={() => game.select(r, c)}
                ref={isSel ? focusIfInGrid : undefined}
              >
                {number && <span className="cw-num">{number}</span>}
                <span className="cw-letter">{letter}</span>
                {revealed && <span className="cw-revealed-dot" aria-hidden="true" />}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/* Keep keyboard focus on the selected square while focus is in the grid
   (arrows and typing move it), never steal it from elsewhere. */
function focusIfInGrid(el) {
  if (el && document.activeElement?.closest?.('.cw-grid') && document.activeElement !== el)
    el.focus()
}

function Keyboard({ game }) {
  return (
    <div className="cw-keyboard" aria-label={t('crossword.keyboard')}>
      {KEYBOARD_ROWS.map((row, i) => (
        <div className="cw-key-row" key={row}>
          {row.split('').map((ch) => (
            <button key={ch} type="button" className="cw-key" onClick={() => game.type(ch)}>
              {ch}
            </button>
          ))}
          {i === KEYBOARD_ROWS.length - 1 && (
            <button
              type="button"
              className="cw-key wide"
              onClick={game.back}
              aria-label={t('crossword.keyboardDelete')}
            >
              <BackspaceIcon size={22} />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
